import { pb, Project, MarketAnalysis, Persona, Positioning, BusinessModel, MvpPlan, MarketingStrategy, ResearchChat, MentorChat, Payment, PromoCode } from './pocketbase';
import { apiLogger } from './logger';
import { API_URL } from './config';
import { getPhRef } from './phRef';
import { markGenerated } from '@/components/feedback/feedbackStorage';
import type { CatalogMethod, CatalogResponse, GenerateRequest, GenerateResponse, MethodologyArtifact } from '@/types/methodologies';
import { courseClaimErrorCode } from './course-claim';
import { goalsForApiResponse, reachGoal } from './analytics';

// Re-export types for convenience
export type { Project, MarketAnalysis, Persona, Positioning, BusinessModel, MvpPlan, MarketingStrategy, ResearchChat, MentorChat, Payment, PromoCode };

// Хелпер для получения ID текущего пользователя
const getUserId = (): string => {
  apiLogger.debug('getUserId called, authStore.isValid:', pb.authStore.isValid);
  apiLogger.debug('authStore.token:', pb.authStore.token ? pb.authStore.token.substring(0, 20) + '...' : 'null');
  apiLogger.debug('authStore.record:', pb.authStore.record);
  if (!pb.authStore.isValid) {
    throw new Error('Сессия истекла. Пожалуйста, войдите снова.');
  }
  const user = pb.authStore.record;
  if (!user?.id) {
    throw new Error('Данные пользователя не загружены. Обновите страницу.');
  }
  apiLogger.debug('userId:', user.id);
  return user.id;
};

// Хелпер для получения токена
const getAuthHeader = (): HeadersInit => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${pb.authStore.token}`,
});

// Безопасный парсинг JSON: если сервер вернул HTML (напр. 504 от nginx) — кидает понятную ошибку
async function parseErrorJson(response: Response): Promise<{ message?: string; error?: string }> {
  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    try {
      return await response.json();
    } catch {
      // Заголовок обещал JSON, а тело пустое или битое — не роняем на этом весь запрос
    }
  }
  return { message: `Ошибка сервера (${response.status}). Попробуйте ещё раз.` };
}

/**
 * Запрос считается «пользователь что-то сгенерировал»: любой POST/PUT в /api/ai/*
 * (анализ рынка, персоны, позиционирование, бизнес-модель, стратегия, MVP, финмодель,
 * исследовательский и менторский чат) плюс сборка и правка сайта в билдере.
 *
 * Нужно для гейта проактивной формы обратной связи: не спрашиваем отзыв у того,
 * кто ещё ничего не создал. DELETE не считается — удаление персоны не генерация.
 */
export function isGenerationRequest(url: string, method?: string): boolean {
  const verb = (method || 'GET').toUpperCase();
  if (verb !== 'POST' && verb !== 'PUT') return false;

  const path = url.replace(API_URL, '');
  if (path.startsWith('/api/ai/')) return true;
  return /^\/api\/builder\/[^/]+\/(generate|chat)$/.test(path);
}

// Обёртка над fetch: проверяет валидность сессии до запроса и обрабатывает 401
async function apiFetch(url: string, options: RequestInit = {}): Promise<Response> {
  if (!pb.authStore.isValid) {
    pb.authStore.clear();
    window.location.href = '/login';
    throw new Error('Сессия истекла. Пожалуйста, войдите снова.');
  }
  const response = await fetch(url, options);
  if (response.status === 401) {
    pb.authStore.clear();
    window.location.href = '/login';
    throw new Error('Сессия истекла. Пожалуйста, войдите снова.');
  }
  if (response.ok && isGenerationRequest(url, options.method)) {
    markGenerated();
  }
  void trackApiGoals(url, options, response);
  return response;
}

// Отправляет цели Метрики по завершённому запросу. Тело ответа читается из клона,
// чтобы не съесть его у вызывающего кода; любая ошибка аналитики глушится.
async function trackApiGoals(url: string, options: RequestInit, response: Response): Promise<void> {
  try {
    if ((options.method || 'GET').toUpperCase() !== 'POST') return;
    let body: Record<string, unknown> | null = null;
    if (typeof options.body === 'string') {
      try { body = JSON.parse(options.body); } catch { body = null; }
    }
    let errorCode: string | undefined;
    if (response.status === 403) {
      const data = await parseErrorJson(response.clone());
      errorCode = data.error;
    }
    for (const hit of goalsForApiResponse(options.method, url.replace(API_URL, ''), response.status, body, errorCode)) {
      reachGoal(hit.goal, hit.params);
    }
  } catch {
    // Аналитика не должна ломать запросы.
  }
}

// API_URL imported from config.ts

// Хелпер для валидации и экранирования ID (защита от injection)
const validateId = (id: string, fieldName: string = 'id'): string => {
  // UUID v4 format: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx
  // Pocketbase ID format: 15 символов (alphanumeric)
  const uuidRegex = /^[a-zA-Z0-9]{15}$/;
  if (!uuidRegex.test(id)) {
    throw new Error(`Недопустимое значение ${fieldName}: ${id}`);
  }
  return id;
};

// Безопасный хелпер для создания фильтров
const createFilter = (conditions: Record<string, string>): string => {
  const validated = Object.entries(conditions).map(([key, value]) => {
    const safeValue = validateId(value, key);
    return `${key} = "${safeValue}"`;
  });
  return validated.join(' && ');
};

// ==================== PROJECTS API ====================

export const projectsApi = {
  async list(): Promise<Project[]> {
    // listRule в коллекции фильтрует по user автоматически
    return await pb.collection('projects').getFullList<Project>({
      sort: '-created',
    });
  },

  async getOne(projectId: string): Promise<Project> {
    return await pb.collection('projects').getOne<Project>(projectId);
  },

  async create(data: {
    name: string;
    market?: string;
    stage?: Project['stage'];
    description?: string;
    revenue?: string;
  }): Promise<Project> {
    const response = await apiFetch(`${API_URL}/api/projects`, {
      method: 'POST',
      headers: getAuthHeader(),
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const err = await parseErrorJson(response);
      throw new Error(err.message || err.error || 'Failed to create project');
    }
    return response.json();
  },

  async update(projectId: string, data: Partial<Project>): Promise<Project> {
    return await pb.collection('projects').update<Project>(projectId, data);
  },

  async delete(projectId: string): Promise<void> {
    await pb.collection('projects').delete(projectId);
  },

  async setActive(projectId: string, isActive: boolean): Promise<Project> {
    return await pb.collection('projects').update<Project>(projectId, { is_active: isActive });
  },
};

// ==================== MARKET ANALYSES API ====================

export const marketAnalysesApi = {
  async list(projectId?: string): Promise<MarketAnalysis[]> {
    const userId = getUserId();
    const baseFilter = projectId
      ? createFilter({ user: userId, project: projectId })
      : createFilter({ user: userId });
    const filter = `${baseFilter} && status = "completed"`;
    return await pb.collection('market_analyses').getFullList<MarketAnalysis>({
      filter,
      sort: 'created',
      expand: 'project',
    });
  },

  async getOne(id: string): Promise<MarketAnalysis> {
    return await pb.collection('market_analyses').getOne<MarketAnalysis>(id);
  },

  // AI генерация идёт через Hono backend
  async generate(
    projectId: string,
    data: {
      productDescription: string;
      keywords?: string;
      competitors?: string;
      advantage?: string;
      comment?: string;
      lang?: string;
      /** Student plan: regenerate this analysis in place instead of adding a new one. */
      replaceId?: string;
    }
  ): Promise<{ success: boolean; pending?: boolean; analysis: MarketAnalysis }> {
    const response = await apiFetch(`${API_URL}/api/ai/market-analysis`, {
      method: 'POST',
      headers: getAuthHeader(),
      body: JSON.stringify({ projectId, ...data }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Ошибка генерации анализа рынка');
    }

    return response.json();
  },

  async delete(id: string): Promise<void> {
    await pb.collection('market_analyses').delete(id);
  },
};

// ==================== PERSONAS API ====================

export const personasApi = {
  async list(projectId?: string): Promise<Persona[]> {
    const userId = getUserId();
    const filter = projectId
      ? createFilter({ user: userId, project: projectId })
      : createFilter({ user: userId });
    return await pb.collection('personas').getFullList<Persona>({
      filter,
      sort: 'created',
      expand: 'project',
    });
  },

  async getOne(id: string): Promise<Persona> {
    return await pb.collection('personas').getOne<Persona>(id);
  },

  // AI генерация идёт через Hono backend
  async generate(
    projectId: string,
    data: {
      marketAnalysisId?: string;
      customPrompt?: string;
    }
  ): Promise<Persona> {
    const response = await apiFetch(`${API_URL}/api/ai/personas`, {
      method: 'POST',
      headers: getAuthHeader(),
      body: JSON.stringify({ projectId, ...data }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Ошибка генерации персоны');
    }

    return response.json();
  },

  async delete(id: string): Promise<void> {
    await pb.collection('personas').delete(id);
  },
};

// ==================== POSITIONINGS API ====================

export const positioningsApi = {
  async list(projectId?: string): Promise<Positioning[]> {
    const userId = getUserId();
    const filter = projectId
      ? createFilter({ user: userId, project: projectId })
      : createFilter({ user: userId });
    return await pb.collection('positionings').getFullList<Positioning>({
      filter,
      sort: 'created',
      expand: 'project,market_analysis,persona',
    });
  },

  async getOne(id: string): Promise<Positioning> {
    return await pb.collection('positionings').getOne<Positioning>(id, {
      expand: 'market_analysis,persona',
    });
  },

  // AI генерация идёт через Hono backend
  async generate(
    projectId: string,
    data: {
      marketAnalysisId?: string;
      personaId?: string;
    }
  ): Promise<Positioning> {
    const response = await apiFetch(`${API_URL}/api/ai/positioning`, {
      method: 'POST',
      headers: getAuthHeader(),
      body: JSON.stringify({ projectId, ...data }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Ошибка генерации позиционирования');
    }

    return response.json();
  },

  async delete(id: string): Promise<void> {
    await pb.collection('positionings').delete(id);
  },
};

// ==================== BUSINESS MODELS API ====================

export const businessModelsApi = {
  async list(projectId?: string): Promise<BusinessModel[]> {
    const userId = getUserId();
    const filter = projectId
      ? createFilter({ user: userId, project: projectId })
      : createFilter({ user: userId });
    return await pb.collection('business_models').getFullList<BusinessModel>({
      filter,
      sort: 'created',
      expand: 'project,positioning,market_analysis',
    });
  },

  async getOne(id: string): Promise<BusinessModel> {
    return await pb.collection('business_models').getOne<BusinessModel>(id, {
      expand: 'positioning,market_analysis',
    });
  },

  async generate(
    projectId: string,
    data: {
      positioningId?: string;
      marketAnalysisId?: string;
      revenueHint?: string;
      targetMarketSize?: string;
      comment?: string;
    }
  ): Promise<BusinessModel> {
    const response = await apiFetch(`${API_URL}/api/ai/business-model`, {
      method: 'POST',
      headers: getAuthHeader(),
      body: JSON.stringify({ projectId, ...data }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Ошибка генерации бизнес-модели');
    }

    return response.json();
  },

  async delete(id: string): Promise<void> {
    await pb.collection('business_models').delete(id);
  },
};

// ==================== MARKETING STRATEGIES API ====================

export const marketingStrategiesApi = {
  async list(projectId?: string): Promise<MarketingStrategy[]> {
    const userId = getUserId();
    const baseFilter = projectId
      ? createFilter({ user: userId, project: projectId })
      : createFilter({ user: userId });
    const filter = `${baseFilter} && status = "completed"`;
    return await pb.collection('marketing_strategies').getFullList<MarketingStrategy>({
      filter,
      sort: 'created',
      expand: 'project',
    });
  },

  async getOne(id: string): Promise<MarketingStrategy> {
    return await pb.collection('marketing_strategies').getOne<MarketingStrategy>(id, {
      expand: 'project',
    });
  },

  async generate(
    projectId: string,
    data: {
      budget: string;
      mainGoal: string;
      teamSize: string;
      currentChannels?: string;
      marketAnalysisId?: string;
      personaId?: string;
      positioningId?: string;
      comment?: string;
      lang?: string;
      /** Student plan: regenerate this strategy in place instead of adding a new one. */
      replaceId?: string;
    }
  ): Promise<{ success: boolean; pending?: boolean; strategy: MarketingStrategy }> {
    const response = await apiFetch(`${API_URL}/api/ai/marketing-strategy`, {
      method: 'POST',
      headers: getAuthHeader(),
      body: JSON.stringify({ projectId, ...data }),
    });

    if (!response.ok) {
      const error = await response.json() as Record<string, any>;
      const detail = error.detail ? ` (${error.detail})` : '';
      throw new Error((error.message || 'Ошибка генерации маркетинговой стратегии') + detail);
    }

    return response.json();
  },

  async delete(id: string): Promise<void> {
    await pb.collection('marketing_strategies').delete(id);
  },
};

// ==================== MVP PLANS API ====================

export const mvpPlansApi = {
  async list(projectId?: string): Promise<MvpPlan[]> {
    const userId = getUserId();
    const filter = projectId
      ? createFilter({ user: userId, project: projectId })
      : createFilter({ user: userId });
    return await pb.collection('mvp_plans').getFullList<MvpPlan>({
      filter,
      sort: 'created',
      expand: 'project,positioning',
    });
  },

  async getOne(id: string): Promise<MvpPlan> {
    return await pb.collection('mvp_plans').getOne<MvpPlan>(id, {
      expand: 'positioning',
    });
  },

  // AI генерация идёт через Hono backend
  async generate(
    projectId: string,
    data: {
      positioningId?: string;
    }
  ): Promise<MvpPlan> {
    const response = await apiFetch(`${API_URL}/api/ai/mvp-plans`, {
      method: 'POST',
      headers: getAuthHeader(),
      body: JSON.stringify({ projectId, ...data }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Ошибка генерации MVP плана');
    }

    return response.json();
  },

  async delete(id: string): Promise<void> {
    await pb.collection('mvp_plans').delete(id);
  },
};

// ==================== RESEARCH CHATS API ====================

export const researchChatsApi = {
  async list(projectId?: string): Promise<ResearchChat[]> {
    const userId = getUserId();
    const filter = projectId
      ? createFilter({ user: userId, project: projectId })
      : createFilter({ user: userId });
    return await pb.collection('research_chats').getFullList<ResearchChat>({
      filter,
      sort: '-updated',
      expand: 'project,persona',
    });
  },

  async getOne(id: string): Promise<ResearchChat> {
    return await pb.collection('research_chats').getOne<ResearchChat>(id, {
      expand: 'persona',
    });
  },

  async create(projectId: string, personaId: string): Promise<ResearchChat> {
    const userId = getUserId();
    return await pb.collection('research_chats').create<ResearchChat>({
      user: userId,
      project: projectId,
      persona: personaId,
      messages: [],
    });
  },

  // Отправка сообщения через Hono backend (AI ответ)
  async sendMessage(
    chatId: string,
    message: string
  ): Promise<{ reply: string; chat: ResearchChat }> {
    const response = await apiFetch(`${API_URL}/api/ai/research-chat`, {
      method: 'POST',
      headers: getAuthHeader(),
      body: JSON.stringify({ chatId, message }),
    });

    if (!response.ok) {
      const error = await parseErrorJson(response);
      throw new Error(error.message || error.error || 'Ошибка отправки сообщения');
    }

    return response.json();
  },

  async updateTitle(id: string, title: string): Promise<void> {
    await pb.collection('research_chats').update(id, { title });
  },

  async delete(id: string): Promise<void> {
    await pb.collection('research_chats').delete(id);
  },
};

// ==================== MENTOR CHATS API ====================

export const mentorChatsApi = {
  async list(): Promise<MentorChat[]> {
    const userId = getUserId();
    return await pb.collection('mentor_chats').getFullList<MentorChat>({
      filter: createFilter({ user: userId }),
      sort: '-updated',
    });
  },

  async getOne(id: string): Promise<MentorChat> {
    return await pb.collection('mentor_chats').getOne<MentorChat>(id);
  },

  async create(title?: string): Promise<MentorChat> {
    const userId = getUserId();
    return await pb.collection('mentor_chats').create<MentorChat>({
      user: userId,
      title: title || 'Новый чат',
      messages: [],
      message_count: 0,
    });
  },

  async updateTitle(id: string, title: string): Promise<MentorChat> {
    return await pb.collection('mentor_chats').update<MentorChat>(id, { title });
  },

  // Отправка сообщения через Hono backend (AI ответ)
  async sendMessage(
    chatId: string,
    message: string,
    attachments?: File[],
    artifacts?: Array<{ id: string; type: string }>,
    projectId?: string,
  ): Promise<{ reply: string; artifacts?: Array<{ type: string; recordId: string; status: string; summary?: string }>; chat: MentorChat }> {
    const hasFiles = attachments && attachments.length > 0;

    if (hasFiles) {
      const formData = new FormData();
      formData.append('chatId', chatId);
      formData.append('message', message);
      if (projectId) formData.append('projectId', projectId);
      attachments.forEach((file) => {
        formData.append('attachments', file);
      });
      if (artifacts && artifacts.length > 0) {
        formData.append('artifacts', JSON.stringify(artifacts));
      }

      const response = await apiFetch(`${API_URL}/api/ai/mentor-chat`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${pb.authStore.token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const error = await parseErrorJson(response);
        throw new Error(error.message || error.error || 'Ошибка отправки сообщения');
      }
      return response.json();
    }

    const response = await apiFetch(`${API_URL}/api/ai/mentor-chat`, {
      method: 'POST',
      headers: {
        ...getAuthHeader(),
      },
      body: JSON.stringify({
        chatId,
        message,
        ...(projectId ? { projectId } : {}),
        ...(artifacts && artifacts.length > 0 ? { artifacts } : {}),
      }),
    });

    if (!response.ok) {
      const error = await parseErrorJson(response);
      throw new Error(error.message || error.error || 'Ошибка отправки сообщения');
    }
    return response.json();
  },

  async delete(id: string): Promise<void> {
    await pb.collection('mentor_chats').delete(id);
  },

  // Подсчёт сообщений пользователя (для лимита на бесплатном тарифе)
  async getUserMessageCount(): Promise<number> {
    const userId = getUserId();
    const chats = await pb.collection('mentor_chats').getFullList<MentorChat>({
      filter: createFilter({ user: userId }),
    });
    return chats.reduce((sum, chat) => sum + (chat.message_count || 0), 0);
  },
};

// ==================== CHAT MESSAGES API ====================

export interface ChatMessage {
  id: string;
  chat_id: string;
  chat_type: 'research' | 'mentor';
  role: 'user' | 'assistant';
  content: string;
  metadata?: Record<string, any>;
  created: string;
  updated: string;
}

export interface ChatMessagesResponse {
  messages: ChatMessage[];
  page: number;
  perPage: number;
  totalPages: number;
  totalItems: number;
}

export const chatMessagesApi = {
  async list(
    chatId: string,
    chatType: 'research' | 'mentor',
    page = 1,
    perPage = 50
  ): Promise<ChatMessagesResponse> {
    const params = new URLSearchParams({
      chatId,
      chatType,
      page: String(page),
      perPage: String(perPage),
    });

    const response = await apiFetch(`${API_URL}/api/chat-messages?${params}`, {
      method: 'GET',
      headers: getAuthHeader(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || error.error || 'Ошибка загрузки сообщений');
    }

    return response.json();
  },
};

// ==================== PAYMENTS API ====================

export const paymentsApi = {
  // Создание платежа через Hono backend (Робокасса)
  async createPayment(data: {
    subscriptionType: 'basic' | 'full' | 'creator' | 'top_creator';
    billingPeriod?: 'monthly' | 'yearly';
    promoCode?: string;
    recurring?: boolean;
    consentGiven?: boolean;
    consentText?: string;
  }): Promise<{ paymentUrl: string; invoiceId: string; amount: number; iframeParams: RobokassaParams }> {
    // Read ph_ref from localStorage first, then fall back to shared cookie
    // (handles cleared localStorage / private mode / DevTools tampering).
    const phRef = getPhRef();
    const response = await apiFetch(`${API_URL}/api/robokassa/create-payment`, {
      method: 'POST',
      headers: getAuthHeader(),
      body: JSON.stringify({ ...data, ...(phRef ? { ph_ref: phRef } : {}) }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Ошибка создания платежа');
    }

    return response.json();
  },

  async checkPaymentStatus(invId: string): Promise<{ status: 'pending' | 'success' | 'failed'; subscriptionTier?: string }> {
    const response = await apiFetch(`${API_URL}/api/robokassa/payment-status/${invId}`, {
      headers: getAuthHeader(),
    });
    if (!response.ok) throw new Error('Failed to check payment status');
    return response.json();
  },
};

// ==================== PROMO CODES API ====================

export const promoCodesApi = {
  async validate(code: string): Promise<PromoCode | null> {
    const response = await apiFetch(`${API_URL}/api/promo-codes/validate`, {
      method: 'POST',
      headers: getAuthHeader(),
      body: JSON.stringify({ code }),
    });

    if (!response.ok) {
      return null;
    }

    return response.json();
  },

  async apply(code: string): Promise<{ success: boolean; message: string }> {
    const response = await apiFetch(`${API_URL}/api/promo-codes/apply`, {
      method: 'POST',
      headers: getAuthHeader(),
      body: JSON.stringify({ code }),
    });

    return response.json();
  },
};

// ==================== FEEDBACK API ====================

export interface FeedbackInput {
  rating: number;
  question_answer?: string;
  comment?: string;
  source?: 'sidebar' | 'proactive';
  page?: string;
}

export const feedbackApi = {
  async submit(data: FeedbackInput): Promise<{ success: boolean; id: string }> {
    const response = await apiFetch(`${API_URL}/api/feedback`, {
      method: 'POST',
      headers: getAuthHeader(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const err = await parseErrorJson(response);
      throw new Error(err.message || err.error || 'Не удалось отправить отзыв');
    }

    return response.json();
  },
};

// ==================== SUBSCRIPTION API ====================

export interface CourseEntitlementStatus { active: boolean; grantedAt: string | null }
export interface CourseUsage { artifacts: Record<string, { used: number; limit: number }>; messages: { used: number; limit: number }; editsPerArtifact?: number }

export const courseEntitlementApi = {
  async usage(): Promise<CourseUsage> {
    const response = await apiFetch(`${API_URL}/api/course-entitlement/usage`, { headers: getAuthHeader() });
    if (!response.ok) throw new Error('Не удалось загрузить лимиты');
    return response.json();
  },
  async status(): Promise<CourseEntitlementStatus> {
    const response = await apiFetch(`${API_URL}/api/course-entitlement/status`, { headers: getAuthHeader() });
    if (!response.ok) throw new Error('Не удалось проверить студенческий доступ');
    return response.json();
  },
  /**
   * No token and no body: the claim sits in the HttpOnly `course_claim` cookie the
   * handoff set, so the page never holds it. `credentials: 'include'` is what carries
   * that cookie when the API lives on another origin, as it does in development.
   */
  async claim(): Promise<CourseEntitlementStatus> {
    const response = await apiFetch(`${API_URL}/api/course-entitlement/claim`, {
      method: 'POST', headers: getAuthHeader(), credentials: 'include',
    });
    if (!response.ok) throw new Error(courseClaimErrorCode(response.status));
    return response.json();
  },
};

export interface SubscriptionStatus {
  tier: 'free' | 'basic' | 'full' | 'creator' | 'top_creator';
  isActive: boolean;
  isCancelled: boolean;
  isLegacyOneTime: boolean;
  endDate?: string;
  daysRemaining?: number;
  billingPeriod?: 'monthly' | 'yearly' | null;
  nextBillingDate?: string | null;
}

export const subscriptionApi = {
  async getStatus(): Promise<SubscriptionStatus> {
    if (!pb.authStore.record) {
      throw new Error('Пользователь не авторизован');
    }
    const response = await apiFetch(`${API_URL}/api/subscription/status`, {
      method: 'GET',
      headers: getAuthHeader(),
    });

    if (!response.ok) {
      const error = await parseErrorJson(response);
      throw new Error(error.message || error.error || 'Ошибка загрузки статуса подписки');
    }

    return response.json();
  },

  async cancel(): Promise<void> {
    const response = await apiFetch(`${API_URL}/api/subscription/cancel`, {
      method: 'POST',
      headers: getAuthHeader(),
    });

    if (!response.ok) {
      const error = await parseErrorJson(response);
      throw new Error(error.message || error.error || 'Ошибка отмены подписки');
    }
  },
};

// ==================== AI API (unified interface) ====================

export const aiApi = {
  async generateMarketAnalysis(
    projectId: string,
    data: {
      product_description: string;
      keywords?: string;
      competitors?: string;
      advantage?: string;
      comment?: string;
      lang?: string;
    }
  ): Promise<MarketAnalysis> {
    const response = await apiFetch(`${API_URL}/api/ai/market-analysis`, {
      method: 'POST',
      headers: getAuthHeader(),
      body: JSON.stringify({ projectId, ...data }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Ошибка генерации анализа рынка');
    }

    return response.json();
  },

  async generatePersonas(
    projectId: string,
    data: {
      target_audience?: string;
      market_analysis_id?: string;
      gender?: string;
      age?: string;
      lang?: string;
    }
  ): Promise<{ success: boolean; persona: Persona }> {
    const response = await apiFetch(`${API_URL}/api/ai/personas`, {
      method: 'POST',
      headers: getAuthHeader(),
      body: JSON.stringify({ projectId, ...data }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Ошибка генерации персоны');
    }

    return response.json();
  },

  async updatePersona(
    personaId: string,
    data: {
      target_audience?: string;
      market_analysis_id?: string;
      gender?: string;
      age?: string;
      lang?: string;
    }
  ): Promise<{ success: boolean; persona: Persona }> {
    const response = await apiFetch(`${API_URL}/api/ai/personas/${personaId}`, {
      method: 'PUT',
      headers: getAuthHeader(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Ошибка обновления персоны');
    }

    return response.json();
  },

  async deletePersona(personaId: string): Promise<{ success: boolean }> {
    const response = await apiFetch(`${API_URL}/api/ai/personas/${personaId}`, {
      method: 'DELETE',
      headers: getAuthHeader(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Ошибка удаления персоны');
    }

    return response.json();
  },

  async generatePositioning(
    projectId: string,
    data: {
      market_analysis_id?: string;
      persona_id?: string;
      product_name?: string;
      unique_value?: string;
      competitors?: string;
      lang?: string;
    }
  ): Promise<Positioning> {
    const response = await apiFetch(`${API_URL}/api/ai/positioning`, {
      method: 'POST',
      headers: getAuthHeader(),
      body: JSON.stringify({ projectId, ...data }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Ошибка генерации позиционирования');
    }

    return response.json();
  },

  async generateMvpPlan(
    projectId: string,
    data: {
      positioning_id?: string;
      product_description?: string;
      target_features?: string;
      budget?: string;
      timeline?: string;
      tech_stack?: string;
    }
  ): Promise<MvpPlan> {
    const response = await apiFetch(`${API_URL}/api/ai/mvp-plans`, {
      method: 'POST',
      headers: getAuthHeader(),
      body: JSON.stringify({ projectId, ...data }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Ошибка генерации MVP плана');
    }

    return response.json();
  },

  async researchChat(
    projectId: string,
    personaId: string,
    message: string,
    chatId?: string,
    lang?: string
  ): Promise<{ reply: string; chat: ResearchChat }> {
    const response = await apiFetch(`${API_URL}/api/ai/research-chat`, {
      method: 'POST',
      headers: getAuthHeader(),
      body: JSON.stringify({ projectId, personaId, message, chatId, lang }),
    });

    if (!response.ok) {
      const error = await parseErrorJson(response);
      throw new Error(error.message || error.error || 'Ошибка отправки сообщения');
    }

    return response.json();
  },
};

// ==================== FINANCIAL MODELS API ====================

import type { FinancialModelRecord, FinancialModelInputs, MonthlyData, FinancialModelSummary, BusinessModelType } from '@/types/financial-model';

export const financialModelsApi = {
  async list(projectId?: string): Promise<FinancialModelRecord[]> {
    const userId = getUserId();
    const filter = projectId
      ? createFilter({ user: userId, project: projectId })
      : createFilter({ user: userId });
    return await pb.collection('financial_models').getFullList<FinancialModelRecord>({
      filter,
      sort: '-created',
      expand: 'project',
    });
  },

  async create(data: {
    project: string;
    model_name: string;
    model_type: BusinessModelType;
    input_data: FinancialModelInputs;
    calculated_data: { months: MonthlyData[]; summary: FinancialModelSummary };
  }): Promise<FinancialModelRecord> {
    const userId = getUserId();
    return await pb.collection('financial_models').create<FinancialModelRecord>({
      user: userId,
      project: data.project,
      model_name: data.model_name,
      model_type: data.model_type,
      input_data: data.input_data,
      calculated_data: data.calculated_data,
      status: 'draft',
    });
  },

  async getOne(id: string): Promise<FinancialModelRecord> {
    return await pb.collection('financial_models').getOne<FinancialModelRecord>(id);
  },

  async update(id: string, data: Partial<FinancialModelRecord>): Promise<FinancialModelRecord> {
    return await pb.collection('financial_models').update<FinancialModelRecord>(id, data);
  },

  async requestAiRecommendations(financialModelId: string): Promise<FinancialModelRecord> {
    const response = await apiFetch(`${API_URL}/api/ai/financial-model`, {
      method: 'POST',
      headers: getAuthHeader(),
      body: JSON.stringify({ financialModelId }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Ошибка получения рекомендаций');
    }

    const body = await response.json() as { success: boolean; financialModel: FinancialModelRecord };
    return body.financialModel;
  },

  async delete(id: string): Promise<void> {
    await pb.collection('financial_models').delete(id);
  },
};

// ==================== METHODOLOGIES API ====================
//
// Catalog and generation go through the Hono backend (registry lookup + quota
// ledger). Reads/list/delete go straight to PocketBase — the collection's
// userOwnedRules already scope everything to the current user, same as every
// other artifact type here.

/** Strips characters that would break out of the quoted filter value. Method
 * ids are short slugs (e.g. "product-vision"), not PocketBase record ids, so
 * the generic `validateId`/`createFilter` 15-char check does not apply. */
const escapeFilterValue = (value: string): string => value.replace(/["\\]/g, '');

export const methodologiesApi = {
  async catalog(): Promise<CatalogMethod[]> {
    const response = await apiFetch(`${API_URL}/api/ai/methodologies/catalog`, {
      method: 'GET',
      headers: getAuthHeader(),
    });

    if (!response.ok) {
      const error = await parseErrorJson(response);
      throw new Error(error.message || error.error || 'Не удалось загрузить каталог методологий');
    }

    const body = await response.json() as CatalogResponse;
    return body.methods;
  },

  async generate(data: GenerateRequest): Promise<MethodologyArtifact> {
    const response = await apiFetch(`${API_URL}/api/ai/methodologies`, {
      method: 'POST',
      headers: getAuthHeader(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await parseErrorJson(response);
      throw new Error(error.message || error.error || 'Не удалось запустить генерацию');
    }

    const body = await response.json() as GenerateResponse;
    return body.artifact;
  },

  async list(projectId?: string, method?: string): Promise<MethodologyArtifact[]> {
    const userId = getUserId();
    const baseFilter = projectId
      ? createFilter({ user: userId, project: projectId })
      : createFilter({ user: userId });
    const filter = method
      ? `${baseFilter} && method = "${escapeFilterValue(method)}"`
      : baseFilter;
    return await pb.collection('methodology_artifacts').getFullList<MethodologyArtifact>({
      filter,
      sort: '-created',
    });
  },

  async getOne(id: string): Promise<MethodologyArtifact> {
    return await pb.collection('methodology_artifacts').getOne<MethodologyArtifact>(id);
  },

  async delete(id: string): Promise<void> {
    await pb.collection('methodology_artifacts').delete(id);
  },
};

// ==================== BUILDER API ====================

// В отличие от остальных артефактов билдер не читается напрямую из PocketBase:
// резервирование адреса, нумерация версий и лимиты требуют серверной проверки,
// поэтому всё идёт через /api/builder — включая чтение, чтобы не держать две тропы.

export type BuildKind = 'landing' | 'deck' | 'app';
export type BuildStatus = 'draft' | 'published' | 'unpublished' | 'suspended';

export interface Build {
  id: string;
  kind: BuildKind;
  name: string;
  slug: string;
  status: BuildStatus;
  project: string | null;
  brief: Record<string, unknown>;
  settings: Record<string, unknown>;
  sourceArtifacts: Record<string, unknown>;
  publishedAt: string | null;
  /** Публичный адрес. null, пока сайт не опубликован. */
  url: string | null;
  /** Сколько заявок пришло (без спама). */
  leads: number;
  /** Сколько версий сохранено. 0 — черновик действительно пуст. */
  versions: number;
  created: string;
  updated: string;
}

export interface BuildLead {
  id: string;
  data: Record<string, string>;
  /** utm-метки визита, если были. */
  source: Record<string, string> | null;
  referrer: string | null;
  isSpam: boolean;
  created: string;
}

export interface BuildVersionMeta {
  id: string;
  version: number;
  author: 'ai' | 'user';
  change_summary?: string;
  created: string;
}

/** Схема страницы. Структура описана в backend/src/services/builder/schema.ts. */
export interface PageSchema {
  version: number;
  theme: Record<string, string>;
  seo: { title: string; description: string; ogImage?: string };
  sections: Array<{
    id: string;
    type: string;
    variant: string;
    props: Record<string, unknown>;
    visible: boolean;
  }>;
}

/**
 * Типы слайдов презентации. Порядок повторяет `SLIDE_ORDER` бэкенда
 * (backend/src/services/builder/deck/catalog.ts) — он же порядок повествования питча.
 */
export const DECK_SLIDE_TYPES = [
  'cover',
  'problem',
  'solution',
  'market',
  'product',
  'business-model',
  'unit-economics',
  'traction',
  'competitors',
  'gtm',
  'team',
  'roadmap',
  'financials',
  'ask',
] as const;

export type DeckSlideType = (typeof DECK_SLIDE_TYPES)[number];

export interface DeckSlide {
  id: string;
  type: DeckSlideType;
  layout: string;
  props: Record<string, unknown>;
  visible: boolean;
  /** Заметки спикера. В PDF v1 не идут. */
  notes?: string;
}

/**
 * Схема презентации. Структура — backend/src/services/builder/deck/schema.ts.
 * Лежит в том же `builder_versions.content`, что и схема страницы: что именно там
 * лежит, говорит `build.kind`.
 */
export interface DeckSchema {
  version: number;
  theme: Record<string, string>;
  seo: { title: string; description: string; ogImage?: string };
  slides: DeckSlide[];
}

/** Содержимое версии билдера: страница или презентация — по `build.kind`. */
export type BuilderContent = PageSchema | DeckSchema;

/** Сужение по форме: у презентации есть `slides`, у страницы — `sections`. */
export function isDeckContent(content: BuilderContent | null): content is DeckSchema {
  return Boolean(content && Array.isArray((content as DeckSchema).slides));
}

/**
 * Какого артефакта не хватает слайду. Бэкенд помечает такой слайд `visible: false`
 * и кладёт в `props._hint` строку вида `artifact-missing:financial_models` —
 * выдумывать цифры в питче нельзя, поэтому вместо них плейсхолдер.
 */
export function missingArtifactOf(slide: DeckSlide): string | null {
  const hint = slide.props._hint;
  if (typeof hint !== 'string') return null;
  const match = /^artifact-missing:(.+)$/.exec(hint);
  return match?.[1] ?? null;
}

export interface BuilderMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  /** Версия, которую создало это сообщение. null — страница не менялась. */
  version: number | null;
  created: string;
}

/** Операции правки. Определение — backend/src/services/builder/edit-ops.ts. */
export type BuilderOp =
  | { op: 'set_text'; sectionId: string; field: string; value: string }
  | { op: 'replace_section_props'; sectionId: string; props: Record<string, unknown> }
  | { op: 'add_section'; sectionType: string; variant?: string; afterId?: string; props: Record<string, unknown> }
  | { op: 'remove_section'; sectionId: string }
  | { op: 'move_section'; sectionId: string; toIndex: number }
  | { op: 'set_visibility'; sectionId: string; visible: boolean }
  | { op: 'set_theme'; patch: Record<string, unknown> }
  | { op: 'set_seo'; patch: Record<string, unknown> }
  // Только для презентации: `sectionId` адресует слайд так же, как секцию страницы.
  | { op: 'set_slide_notes'; sectionId: string; notes: string }
  | { op: 'set_slide_layout'; sectionId: string; layout: string };

export interface DeckShareStatus {
  enabled: boolean;
  /** Публичный адрес презентации. null — ссылка выключена. */
  url: string | null;
  createdAt: string | null;
}

/**
 * HTML презентации по публичной ссылке.
 *
 * Отдельно от `builderApi`: запрос идёт без заголовка авторизации — его открывает
 * инвестор, у которого нет и не должно быть аккаунта.
 */
export async function fetchPublicDeck(
  buildId: string,
  token: string,
): Promise<{ html: string; name: string }> {
  const response = await apiFetch(
    `${API_URL}/api/builder-public/deck/${buildId}?token=${encodeURIComponent(token)}`,
  );

  if (!response.ok) {
    const err = await parseErrorJson(response);
    throw new Error(err.error || 'Презентация не найдена или ссылка отозвана');
  }

  return response.json();
}

async function builderFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const response = await apiFetch(`${API_URL}/api/builder${path}`, {
    headers: getAuthHeader(),
    ...options,
  });

  if (!response.ok) {
    const err = await parseErrorJson(response);
    throw new Error(err.error || err.message || 'Не удалось выполнить запрос');
  }

  return response.json();
}

export const builderApi = {
  /**
   * `publishingEnabled` приходит с сервера, а не выводится из тарифа: пока домен для
   * сайтов не куплен и не настроен, публикация выключена для всех.
   */
  async list(): Promise<{ builds: Build[]; publishingEnabled: boolean }> {
    return builderFetch<{ builds: Build[]; publishingEnabled: boolean }>('');
  },

  async create(data: { name: string; kind?: BuildKind; projectId?: string; brief?: Record<string, unknown> }): Promise<{ build: Build; suggestedSlug: string }> {
    return builderFetch('', { method: 'POST', body: JSON.stringify(data) });
  },

  async get(buildId: string): Promise<{
    build: Build;
    currentVersion: { version: number; author: 'ai' | 'user'; created: string } | null;
    content: BuilderContent | null;
    publishingEnabled: boolean;
  }> {
    return builderFetch(`/${buildId}`);
  },

  async update(buildId: string, data: {
    name?: string;
    slug?: string;
    brief?: Record<string, unknown>;
    settings?: Record<string, unknown>;
  }): Promise<Build> {
    const body = await builderFetch<{ build: Build }>(`/${buildId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
    return body.build;
  },

  async delete(buildId: string): Promise<void> {
    await builderFetch(`/${buildId}`, { method: 'DELETE' });
  },

  async listVersions(buildId: string): Promise<BuildVersionMeta[]> {
    const body = await builderFetch<{ versions: BuildVersionMeta[] }>(`/${buildId}/versions`);
    return body.versions;
  },

  async saveVersion(buildId: string, content: BuilderContent, changeSummary?: string): Promise<{ version: number; created: string }> {
    const body = await builderFetch<{ version: { version: number; created: string } }>(
      `/${buildId}/versions`,
      { method: 'POST', body: JSON.stringify({ content, changeSummary }) },
    );
    return body.version;
  },

  /**
   * Применить правки. Одна тропа и для клика по тексту в превью, и для будущих
   * правок через чат — значит и версионирование у них общее.
   */
  async applyOps(buildId: string, ops: BuilderOp[]): Promise<{ version: number; content: BuilderContent }> {
    const body = await builderFetch<{ version: { version: number }; content: BuilderContent }>(
      `/${buildId}/ops`,
      { method: 'POST', body: JSON.stringify({ ops }) },
    );
    return { version: body.version.version, content: body.content };
  },

  /** Откат добавляет новую версию с прежним содержимым — старая работа не теряется. */
  async restore(buildId: string, version: number): Promise<{ version: number; content: BuilderContent }> {
    const body = await builderFetch<{ version: { version: number }; content: BuilderContent }>(
      `/${buildId}/restore`,
      { method: 'POST', body: JSON.stringify({ version }) },
    );
    return { version: body.version.version, content: body.content };
  },

  /**
   * Сборка страницы по брифу. Занимает ~30 секунд — нужен индикатор прогресса.
   * `degraded: true` означает, что модель не справилась и вернулась базовая
   * структура по брифу: это стартовая точка, а не готовая страница.
   */
  async generate(buildId: string, brief: Record<string, unknown>, lang?: 'ru' | 'en'): Promise<{
    version: { version: number; created: string };
    content: BuilderContent;
    degraded: boolean;
  }> {
    return builderFetch(`/${buildId}/generate`, {
      method: 'POST',
      body: JSON.stringify({ brief, lang }),
    });
  },

  async listMessages(buildId: string): Promise<BuilderMessage[]> {
    const body = await builderFetch<{ messages: BuilderMessage[] }>(`/${buildId}/messages`);
    return body.messages;
  },

  /**
   * Правка обычными словами. `changed: false` означает, что страница осталась
   * прежней — модель задала вопрос или отказалась выдумывать данные.
   */
  async chat(buildId: string, message: string, lang?: 'ru' | 'en'): Promise<{
    reply: string;
    version: number | null;
    content: BuilderContent | null;
    changed: boolean;
  }> {
    return builderFetch(`/${buildId}/chat`, {
      method: 'POST',
      body: JSON.stringify({ message, lang }),
    });
  },

  /** HTML для iframe редактора. Кладётся в srcdoc, поэтому токен не попадает в URL. */
  async preview(buildId: string, version?: number): Promise<{ html: string; version: number }> {
    const query = version ? `?version=${version}` : '';
    return builderFetch(`/${buildId}/preview${query}`);
  },

  /**
   * Ссылка на презентацию. Повторный вызов не выдаёт новый токен — иначе
   * «поделиться» второй раз ломало бы ссылку, отправленную вчера.
   */
  async enableShare(buildId: string): Promise<{ url: string; token: string; createdAt: string }> {
    return builderFetch(`/${buildId}/share`, { method: 'POST', body: '{}' });
  },

  /** Отзыв: старый адрес перестаёт работать сразу и для всех. */
  async revokeShare(buildId: string): Promise<void> {
    await builderFetch(`/${buildId}/share/revoke`, { method: 'POST', body: '{}' });
  },

  async shareStatus(buildId: string): Promise<DeckShareStatus> {
    return builderFetch(`/${buildId}/share/status`);
  },

  async checkSlug(slug: string, buildId?: string): Promise<{ available: boolean; slug?: string; error?: string }> {
    const params = new URLSearchParams({ slug });
    if (buildId) params.set('buildId', buildId);
    return builderFetch(`/slug/check?${params.toString()}`);
  },

  /** Публикация текущей версии. Повторный вызов = перепубликация без простоя. */
  async publish(buildId: string): Promise<{ url: string; version: number }> {
    return builderFetch(`/${buildId}/publish`, { method: 'POST', body: '{}' });
  },

  /** Снятие с публикации. Версии и заявки остаются на месте. */
  async unpublish(buildId: string): Promise<void> {
    await builderFetch(`/${buildId}/unpublish`, { method: 'POST', body: '{}' });
  },

  async listLeads(buildId: string): Promise<BuildLead[]> {
    const body = await builderFetch<{ leads: BuildLead[] }>(`/${buildId}/leads`);
    return body.leads;
  },

  async deleteLead(buildId: string, leadId: string): Promise<void> {
    await builderFetch(`/${buildId}/leads/${leadId}`, { method: 'DELETE' });
  },

  /**
   * Выгрузка заявок в CSV.
   *
   * Файл забирается через fetch с токеном и отдаётся как blob: обычная ссылка ушла бы
   * без заголовка Authorization и вернула 401.
   */
  async downloadLeadsCsv(buildId: string, filename: string): Promise<void> {
    const response = await apiFetch(`${API_URL}/api/builder/${buildId}/leads.csv`, {
      headers: getAuthHeader(),
    });

    if (!response.ok) {
      const err = await parseErrorJson(response);
      throw new Error(err.error || 'Не удалось выгрузить заявки');
    }

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  },
};

// ==================== SITE AUDIT API ====================

/** Одна находка аудита. Определение — backend/src/services/audit/types.ts. */
export interface AuditIssue {
  id: string;
  category: 'runtime' | 'seo' | 'cta' | 'mobile' | 'tracking' | 'speed' | 'offer';
  severity: 'critical' | 'high' | 'medium' | 'low';
  title: string;
  description: string;
  evidence: { kind: string; value: string; location?: string };
  recommendation: string;
  fixPrompt: string;
  /** Есть только у сайтов из билдера: текст для чата редактора. */
  builderAction?: { kind: 'edit-chat'; message: string };
  detector: 'rule' | 'ai';
  ruleId?: string;
}

export interface AuditReport {
  url: string;
  finalUrl: string;
  fetchedAt: string;
  issues: AuditIssue[];
  meta: {
    status: number;
    title: string;
    fetchMs: number;
    sizeBytes: number;
    redirects: number;
    isSpa: boolean;
    lang: string;
    totalIssues: number;
    offerAnalyzed: boolean;
  };
}

export type AuditCategory = AuditIssue['category'];
export type AuditSeverity = AuditIssue['severity'];

export const AUDIT_CATEGORIES: AuditCategory[] = [
  'runtime',
  'seo',
  'cta',
  'mobile',
  'tracking',
  'speed',
  'offer',
];

/** Порядок разбора: сначала то, из-за чего теряют посетителей прямо сейчас. */
export const AUDIT_SEVERITY_ORDER: AuditSeverity[] = ['critical', 'high', 'medium', 'low'];

export interface SiteAudit {
  id: string;
  user: string;
  project?: string;
  build?: string;
  url: string;
  final_url?: string;
  title?: string;
  score?: number;
  issues_count?: number;
  category_scores?: Partial<Record<AuditCategory, number | null>>;
  /** Отсутствует в списке: он запрашивается без тяжёлого поля. */
  report?: AuditReport;
  status: 'pending' | 'completed' | 'failed';
  lang?: string;
  error?: string;
  duration_ms?: number;
  created: string;
  updated: string;
}

export const siteAuditApi = {
  /**
   * Запуск аудита. Отвечает сразу, запись приходит в статусе `pending` —
   * отчёт дописывает очередь на бэкенде, фронт ждёт его поллингом.
   */
  async start(data: {
    url?: string;
    buildId?: string;
    projectId?: string;
    lang?: 'ru' | 'en';
  }): Promise<SiteAudit> {
    const response = await apiFetch(`${API_URL}/api/ai/site-audit`, {
      method: 'POST',
      headers: getAuthHeader(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await parseErrorJson(response);
      throw new Error(error.message || error.error || 'Не удалось запустить аудит');
    }

    const body = (await response.json()) as { audit: SiteAudit };
    return body.audit;
  },

  /**
   * Список без поля `report`: отчёт весит до мегабайта на запись, и тянуть их все
   * ради даты и балла незачем.
   */
  async list(): Promise<SiteAudit[]> {
    return pb.collection('site_audits').getFullList<SiteAudit>({
      filter: createFilter({ user: getUserId() }),
      sort: '-created',
      fields: 'id,url,final_url,title,score,issues_count,status,error,created,updated,project,build',
    });
  },

  async getOne(id: string): Promise<SiteAudit> {
    return pb.collection('site_audits').getOne<SiteAudit>(id);
  },

  async delete(id: string): Promise<void> {
    await pb.collection('site_audits').delete(id);
  },
};

// ==================== TOOLS API (F-08) ====================

export type RuAccess = 'works' | 'vpn' | 'blocked';
export type RuPayment = 'rub_card' | 'aggregator' | 'foreign_card';

export interface ToolRef {
  slug: string;
  name: string;
  logoUrl: string | null;
}

/** What the frontend receives. Mirrors backend/src/services/tools/types.ts `ToolCard` — never contains affiliate_url. */
export interface ToolCard {
  slug: string;
  name: string;
  logoUrl: string | null;
  category: string;
  description_short: string;
  pricing_model: 'free' | 'freemium' | 'paid';
  price_from_rub: number | null;
  price_note: string;
  ru_access: RuAccess;
  ru_payment: RuPayment;
  ru_language: boolean;
  is_russian: boolean;
  ru_alternatives: ToolRef[];
  aggregators: ToolRef[];
  verified_at: string;
  score: number;
  goUrl: string;
}

/** Recipe mode (F-08.1): mirrors backend/src/services/tools/types.ts RecipeView. */
export interface RecipeRoleView {
  id: string;
  title: string;
  hint: string;
  tools: ToolCard[];
  /** Tools of this role hidden by the "only RU-accessible" filter — offer to untick. */
  hiddenByFilter: number;
}

export interface RecipePathView {
  id: string;
  title: string;
  subtitle: string;
  roles: RecipeRoleView[];
}

export interface RecipeView {
  id: string;
  title: string;
  paths: RecipePathView[];
}

export const toolsApi = {
  async search(data: { task: string; projectId?: string; onlyRuAccessible: boolean }): Promise<{ results: ToolCard[]; explainable: boolean; recipe: RecipeView | null }> {
    const response = await apiFetch(`${API_URL}/api/tools/search`, {
      method: 'POST', headers: getAuthHeader(), body: JSON.stringify(data),
    });
    // 429 comes either from our own per-user rate limiter (hono-rate-limiter, fixed with
    // a JSON `message` — see I-3 in the review) or from the AI provider itself being rate
    // limited; 503 covers the whole search pipeline failing (ranker down, project lookup,
    // catalog load). Both sources answer with a Russian `message` — prefer it, falling
    // back to a generic message only when it's missing (e.g. a 504 from nginx).
    if (response.status === 429) {
      const error = await parseErrorJson(response);
      throw new Error(error.message || 'Слишком много запросов. Подождите минуту и попробуйте снова');
    }
    if (response.status === 503) {
      const error = await parseErrorJson(response);
      throw new Error(error.message || 'Не удалось подобрать инструменты. Попробуйте ещё раз');
    }
    if (!response.ok) {
      const error = await parseErrorJson(response);
      throw new Error(error.message || 'Не удалось подобрать инструменты. Попробуйте ещё раз');
    }
    return (await response.json()) as { results: ToolCard[]; explainable: boolean; recipe: RecipeView | null };
  },

  async explain(data: { task: string; projectId: string; slugs: string[] }): Promise<Record<string, string>> {
    const response = await apiFetch(`${API_URL}/api/tools/explain`, {
      method: 'POST', headers: getAuthHeader(), body: JSON.stringify(data),
    });
    if (!response.ok) return {};
    return ((await response.json()) as { explanations: Record<string, string> }).explanations;
  },

  async examples(): Promise<string[]> {
    const response = await apiFetch(`${API_URL}/api/tools/examples`, { headers: getAuthHeader() });
    if (!response.ok) return [];
    return ((await response.json()) as { examples: string[] }).examples;
  },

  /** The redirect needs the token, which a plain link would not send — so ask for the URL, then open it. */
  async go(slug: string): Promise<void> {
    // window.open is called synchronously in the click handler, before any await —
    // otherwise the browser blocks the popup.
    //
    // No 'noopener' feature here: per WHATWG, noopener makes window.open return null
    // in every modern browser, which would make `opened` always falsy and silently
    // navigate the CURRENT app tab away to the tool's site instead of opening a new
    // one. We keep the window handle and sever window.opener ourselves instead — same
    // end result (the external site can't reach back into our tab) without losing the
    // handle we need to set its location.
    const opened = window.open('', '_blank');
    if (opened) opened.opener = null;
    const response = await apiFetch(`${API_URL}/api/tools/go/${encodeURIComponent(slug)}`, {
      headers: { ...getAuthHeader(), Accept: 'application/json' },
    });
    if (!response.ok) {
      opened?.close();
      throw new Error('Не удалось открыть сайт инструмента');
    }
    const { url } = (await response.json()) as { url: string };
    // M-1 (review): the backend already refuses to hand out a non-http(s) target, but a
    // compromised/misbehaving backend must not be able to turn this into a
    // `javascript:`/`data:` navigation just by returning one — check again here too.
    if (!/^https?:\/\//i.test(url)) {
      opened?.close();
      throw new Error('Не удалось открыть сайт инструмента');
    }
    // opened is null only when the browser genuinely blocked the popup — fall back to
    // navigating the current tab rather than losing the redirect entirely.
    if (opened) opened.location.href = url; else window.location.href = url;
  },
};
